<script setup lang="ts">
import type { LogoutChoice } from "./useAccountDialogs";
import VButton from "@verbleif/lib/src/components/Button/VButton.vue";
import { closeReserveClass } from "@verbleif/lib/src/components/Dialog/dialogSizes";
import { useDialogLabel } from "@verbleif/lib/src/composables/Dialog/useDialogLabel";
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Body of the logout choice: a required choice between signing out of this tab's account or of every account in this
 * browser. Rendered in a paddingless alert layer, so it draws its own header, content and footer.
 */
const {
  name,
  otherName = null,
  count,
  choice = null,
  confirmLoading = false,
  setChoice,
} = defineProps<{
  name: string
  /** The account this tab continues as after "Alleen": none when every other session has expired. */
  otherName?: string | null
  count: number
  choice?: LogoutChoice | null
  confirmLoading?: boolean
  setChoice: (choice: LogoutChoice) => void
}>();

const emit = defineEmits<{
  confirm: []
  cancel: []
}>();

const { t } = useI18n();
const TRANSLATION_KEY = "navbar.dropdown";
const groupId = useId();

const titleId = useDialogLabel("title");
const headerReserveClass = closeReserveClass(true);

const options = computed(() => [
  {
    value: "one" as const,
    label: t(`${TRANSLATION_KEY}.logout_only`, { name }),
    hint: otherName
      ? t(`${TRANSLATION_KEY}.logout_only_hint`, { other: otherName })
      : t(`${TRANSLATION_KEY}.logout_only_hint_login`),
  },
  {
    value: "all" as const,
    label: t(`${TRANSLATION_KEY}.logout_all`, { count }),
    hint: t(`${TRANSLATION_KEY}.logout_all_hint`),
  },
]);
</script>

<template>
  <div class="flex w-full flex-col" data-testid="logout-choice">
    <header class="border-b border-gray-100 py-3.5 pl-5 dark:border-dark-700" :class="headerReserveClass">
      <h2 :id="titleId" class="m-0 text-base leading-5 font-semibold text-(--text)">
        {{ t(`${TRANSLATION_KEY}.logout_question`) }}
      </h2>
    </header>

    <fieldset class="m-0 flex min-w-0 flex-col gap-2 border-0 px-5 pt-4 pb-5">
      <legend class="mb-1 p-0 text-sm leading-5 text-gray-700 dark:text-gray-300" data-testid="logout-choice-legend">
        {{ t(`${TRANSLATION_KEY}.logout_choice_label`, { count }) }}<span class="ml-0.5 text-danger-600" aria-hidden="true">*</span>
      </legend>
      <label
        v-for="option in options"
        :key="option.value"
        class="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-3.5 text-left hover:bg-gray-50 has-checked:border-primary-500 has-checked:bg-primary-50 has-focus-visible:ring-2 has-focus-visible:ring-primary-500 dark:border-dark-600 dark:hover:bg-dark-700 dark:has-checked:bg-primary-900/20"
      >
        <input
          type="radio"
          required
          class="mt-0.5 size-4 shrink-0 accent-primary-500 focus-visible:outline-hidden"
          :name="groupId"
          :value="option.value"
          :checked="choice === option.value"
          :data-testid="`logout-choice-${option.value}`"
          @change="setChoice(option.value)"
        >
        <span class="flex min-w-0 flex-col gap-0.5">
          <span class="text-sm leading-5 font-medium text-gray-900 dark:text-gray-100">{{ option.label }}</span>
          <span class="text-xs leading-4 text-gray-500 dark:text-gray-400">{{ option.hint }}</span>
        </span>
      </label>
    </fieldset>

    <footer class="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/60 px-4 py-3 dark:border-dark-700 dark:bg-dark-800/60">
      <VButton variant="gray" rounded data-testid="logout-choice-cancel" @click="emit('cancel')">
        {{ t("base.cancel") }}
      </VButton>
      <VButton
        variant="danger"
        rounded
        :loading="confirmLoading"
        :disabled="choice === null"
        data-testid="logout-choice-submit"
        @click="emit('confirm')"
      >
        {{ t(`${TRANSLATION_KEY}.logout`) }}
      </VButton>
    </footer>
  </div>
</template>
